import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

import styled from "styled-components";

import { screenBelow } from "../../components/BreakPoints";
import { Button } from "../../components/Button";
import { Columns } from "../../components/Columns";
import { IconButton } from "../../components/IconButton";
import { CancelCrossIcon, ThumbUpOutlineIcon } from "../../components/Icons";
import { InfoBox, InfoBoxType } from "../../components/InfoBox";
import { Space } from "../../components/LayoutTokens";
import { Media } from "../../components/Media";
import { Modal } from "../../components/Modal";
import {
  PortableTextBlockRenderer,
  PortableTextComponents,
  PortableTextMarkRenderer,
  PortableTextMediaImageVariant,
  PortableTextObjectRenderer,
  PortableTextTitledTextBlock,
} from "../../components/PortableText";
import { StyledLink as InlineLink } from "../../components/PortableText/Styles";
import { Row } from "../../components/Row";
import { Stack } from "../../components/Stack";
import { Tiles } from "../../components/Tiles";
import { TitledText } from "../../components/TitledText";
import { Tooltip } from "../../components/Tooltip";
import { Body2 } from "../../components/Typography";
import { Typography } from "../../components/Typography/Typography";

/**
 * The in-app message dialog, as a Presentation mock.
 *
 * Two pieces, mirroring how the product is split:
 *
 * - {@link MessageDialog} — the hollow dialog shell (width, closing,
 *   feedback), built only from design-system parts. In the product this shell
 *   lives in the application as `InAppMessageDialog`.
 * - {@link messageComponents} — a demo message element catalog injected into
 *   the design-system Portable Text engine, exactly like the typed cards
 *   injected into the hollow StackedCard shell. In the product the catalog is
 *   application code; the presentation ships this demo copy so the stories
 *   need no app imports.
 */

/* ---- the dialog shell ---------------------------------------------- */

export type MessageDialogSize = "sm" | "md" | "lg";

const DIALOG_WIDTH: Record<MessageDialogSize, string> = {
  sm: "520px",
  md: "720px",
  lg: "880px",
};

/* Feedback — the thumbs up / down slot. Down-vote reuses the up icon,
   rotated (the DS set only ships ThumbUpOutlineIcon). */
const StyledFeedback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 24px 28px;

  ${screenBelow.sm} {
    padding: 4px 24px 24px;
  }

  .fb-question {
    margin-right: 4px;
  }

  .fb-btn svg {
    width: 18px;
    height: 18px;
  }

  .fb-down svg {
    transform: rotate(180deg);
  }
`;

const DialogFeedback = ({
  onVote,
}: {
  onVote?: (vote: "up" | "down") => void;
}): ReactElement => {
  const [voted, setVoted] = useState<"up" | "down" | null>(null);
  const vote = useCallback(
    (value: "up" | "down") => {
      setVoted(value);
      onVote?.(value);
    },
    [onVote]
  );

  return (
    <StyledFeedback>
      {voted ? (
        <Body2 color="secondary">Thanks for your feedback!</Body2>
      ) : (
        <>
          <Body2 color="secondary" className="fb-question">
            Was this helpful?
          </Body2>
          <Tooltip title="Yes, helpful">
            <IconButton
              type="button"
              variant="text gray"
              className="fb-btn"
              aria-label="Yes, helpful"
              onClick={() => vote("up")}
            >
              <ThumbUpOutlineIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="No, not helpful">
            <IconButton
              type="button"
              variant="text gray"
              className="fb-btn fb-down"
              aria-label="No, not helpful"
              onClick={() => vote("down")}
            >
              <ThumbUpOutlineIcon />
            </IconButton>
          </Tooltip>
        </>
      )}
    </StyledFeedback>
  );
};

/* The shell is behaviour only: it sizes itself, closes on click-outside /
   Esc (DS Modal) / the X, and shows the feedback slot when an onVote handler
   is supplied. Content is whatever the caller renders as children — usually
   the Portable Text engine with the demo catalog injected. */
const StyledDialog = styled.div<{ $size: MessageDialogSize }>`
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: calc(100% - 32px);
  max-width: ${({ $size }) => DIALOG_WIDTH[$size]};
  max-height: calc(100vh - 96px);
  margin: 56px auto 40px;
  overflow-y: auto;
  background-color: var(--page-paper-main);
  color: var(--color-theme-900);
  border-radius: 12px;
  box-shadow: var(--shadow-primary);

  ${screenBelow.sm} {
    margin: 16px auto;
  }

  /* X floats in the corner and overlaps the content padding — it does not
     reserve a row of its own. */
  .dlg-close {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 2;
  }

  .dlg-content {
    padding: 40px;
  }

  ${screenBelow.sm} {
    .dlg-content {
      padding: 32px 20px 24px;
    }
  }
`;

export interface MessageDialogProps {
  open: boolean;
  onClose: () => void;
  size?: MessageDialogSize;
  /** Supplying a handler shows the centred helpful / not-helpful vote. */
  onVote?: (vote: "up" | "down") => void;
  children: ReactNode;
}

export const MessageDialog = ({
  open,
  onClose,
  size = "md",
  onVote,
  children,
}: MessageDialogProps): ReactElement => (
  <Modal open={open} onClose={onClose}>
    <StyledDialog $size={size}>
      <div className="dlg-close">
        <Tooltip title="Close">
          <IconButton
            type="button"
            variant="text gray"
            aria-label="Close"
            onClick={onClose}
          >
            <CancelCrossIcon />
          </IconButton>
        </Tooltip>
      </div>
      <div className="dlg-content">{children}</div>
      {onVote ? <DialogFeedback onVote={onVote} /> : null}
    </StyledDialog>
  </Modal>
);

/* ---- doc-link wiring ------------------------------------------------ */

/** How a doc-link mark opens its article (by a slug-like reference). The
    surface that renders the message supplies the handler — in the product it
    closes the dialog and opens the article Sheet. */
export type OpenArticleHandler = (article?: string) => void;

export const DocLinkContext = React.createContext<OpenArticleHandler>(
  () => undefined
);

/* ---- the demo message element catalog ------------------------------- */

/* Inline marks ------------------------------------------------------- */

/* interface-name — the name of an interface element, e.g. the Security page. */
const StyledInterfaceName = styled.span`
  padding: 0 4px;
  border-radius: 4px;
  background-color: var(--color-theme-300);
  color: var(--color-theme-900);
  font-weight: 600;
  white-space: nowrap;
`;
const InterfaceNameMark: PortableTextMarkRenderer = ({ children }) => (
  <StyledInterfaceName>{children}</StyledInterfaceName>
);

/* keyboard-key — a keyboard key cap. */
const StyledKeyboardKey = styled.kbd`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border: 1px solid var(--border-primary);
  border-bottom-width: 2px;
  border-radius: 5px;
  background-color: var(--color-theme-200);
  color: var(--color-theme-900);
  font-family: ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  vertical-align: baseline;
`;
const KeyboardKeyMark: PortableTextMarkRenderer = ({ children }) => (
  <StyledKeyboardKey>{children}</StyledKeyboardKey>
);

/* doc-link — an inline link that opens a full help article. The mark carries
   an article reference; the open behaviour comes from DocLinkContext, so the
   authored content stays declarative. */
/* Built on the engine's inline anchor so a doc-link sits in the sentence
   exactly like a default link mark — same size, weight and line box as the
   surrounding copy — just recoloured. */
const StyledDocLink = styled(InlineLink)`
  color: var(--color-secondary);
  text-decoration-color: var(--color-secondary);
`;
const DocLinkMark: PortableTextMarkRenderer = ({ mark, children }) => {
  const openArticle = useContext(DocLinkContext);
  return (
    <StyledDocLink
      href="#"
      onClick={(event: React.MouseEvent) => {
        event.preventDefault();
        openArticle(mark?.article as string | undefined);
      }}
    >
      {children}
    </StyledDocLink>
  );
};

/* Component objects --------------------------------------------------- */

/* callout — rendered with the DS InfoBox; the catalog keeps its own tone
   vocabulary and maps it to InfoBox types. */
type CalloutTone = "info" | "note" | "success";
const CALLOUT_TYPE: Record<CalloutTone, InfoBoxType> = {
  info: "default",
  note: "note",
  success: "success",
};
const CalloutObject: PortableTextObjectRenderer = ({ node, children }) => (
  <InfoBox
    type={CALLOUT_TYPE[(node.tone as CalloutTone | undefined) ?? "info"]}
    title={node.title as string | undefined}
    showIcon
  >
    {children}
  </InfoBox>
);

/* button-reference — a real DS Button shown for reference as its own block,
   so a message can point at a button instead of screenshotting it. It is
   intentionally non-interactive. */
const StyledButtonReference = styled.div`
  display: flex;

  .c-button {
    pointer-events: none;
  }
`;
type ButtonReferenceVariant =
  | "primary"
  | "secondary"
  | "text colored"
  | "text gray";
const ButtonReferenceObject: PortableTextObjectRenderer = ({ node }) => {
  if (typeof node.label !== "string") {
    return null;
  }

  return (
    <StyledButtonReference aria-hidden="true">
      <Button
        variant={
          (node.variant as ButtonReferenceVariant | undefined) ?? "primary"
        }
        size="small"
        type="button"
        tabIndex={-1}
      >
        {node.label}
      </Button>
    </StyledButtonReference>
  );
};

/* row — a horizontal run of blocks (e.g. two button references). */
const RowObject: PortableTextObjectRenderer = ({ node, children }) => (
  <Row space={(node.space as Space | undefined) ?? "sm"}>{children}</Row>
);

/* tiles — an equal-size mosaic of blocks. */
const TilesObject: PortableTextObjectRenderer = ({ node, children }) => (
  <Tiles
    columns={(node.columns as number | undefined) ?? 3}
    space="md"
    collapseBelow="sm"
  >
    {children}
  </Tiles>
);

/* split — text beside media, with the one layout rule the messages follow:
   screenshots sit on the right, flat illustrations on the left. `widths` is
   authored as [text, media] and reordered with the columns. */
interface SplitMedia {
  src: string;
  alt?: string;
  // Split never carries a video: video media always stays in the vertical flow.
  variant?: PortableTextMediaImageVariant;
  caption?: string;
}
const SplitObject: PortableTextObjectRenderer = ({ node, children }) => {
  const media = node.media as SplitMedia | undefined;
  const widths = (node.widths as number[] | undefined) ?? [1, 1];
  const textColumn = <Stack space="lg">{children}</Stack>;

  // A split without media degrades to its text column instead of crashing.
  if (!media?.src) {
    return textColumn;
  }

  const screenshot = media.variant === "screenshot";

  const mediaColumn = (
    <Media
      src={media.src}
      alt={media.alt}
      variant={media.variant}
      caption={media.caption}
    />
  );

  return (
    <Columns
      widths={screenshot ? widths : [...widths].reverse()}
      space="xl"
      alignY="center"
      collapseBelow="md"
    >
      {screenshot ? textColumn : mediaColumn}
      {screenshot ? mediaColumn : textColumn}
    </Columns>
  );
};

/* Default-block overrides --------------------------------------------- */

/* The engine's defaults use Body 1 primary; the messages are designed on the
   quieter Body 2 secondary voice, so the catalog overrides the text blocks —
   demonstrating the `blocks` half of the injection contract. */
const ParagraphBlock: PortableTextBlockRenderer = ({ children }) => (
  <Body2 color="secondary" lineHeight="loose">
    {children}
  </Body2>
);

const ListItemBlock: PortableTextBlockRenderer = ({ children }) => (
  <Typography variant="Body 2" as="li" color="secondary" lineHeight="loose">
    {children}
  </Typography>
);

/* Titled sections speak in the quiet Header2 subhead voice rather than the
   engine's default Title2 for level 2, and keep a roomier rhythm between a
   titled section's content nodes. */
const TitledTextBlock: PortableTextBlockRenderer = ({ block, children }) => {
  const { title, level } = block as PortableTextTitledTextBlock;

  return (
    <TitledText title={title} variant={level === 1 ? "title1" : "header2"}>
      {children ? <Stack space="sm">{children}</Stack> : undefined}
    </TitledText>
  );
};

/** The demo catalog the presentations inject into the Portable Text engine —
    the stand-in for the application's `messageComponents` map. */
export const messageComponents: PortableTextComponents = {
  marks: {
    "interface-name": InterfaceNameMark,
    "keyboard-key": KeyboardKeyMark,
    "doc-link": DocLinkMark,
  },
  objects: {
    callout: CalloutObject,
    "button-reference": ButtonReferenceObject,
    row: RowObject,
    tiles: TilesObject,
    split: SplitObject,
  },
  blocks: {
    paragraph: ParagraphBlock,
    "list-item": ListItemBlock,
    "titled-text": TitledTextBlock,
  },
};
